/**
 * BankReconciliationPage — Tab-Container Bank-Abgleich
 * (architecture-bank-reconciliation.md §10.1).
 *
 * BR-3 brachte die Tabs „Konten & Importe" (Konten-CRUD + Import-Wizard +
 * Import-Historie) und „Umsätze" (paginierte Liste). BR-6 ergänzt die Tabs
 * „Abgleich" (Matching-UI, Confirm-Flows) und „Ohne Beleg"
 * (`selectTransactionsWithoutReceipt`) — beide führen Matching-Engine
 * (BR-4/BR-5) und Persistenz (`bankApi`-Confirm-Flows) zusammen.
 *
 * Fail-closed: die Route selbst ist nur registriert, wenn
 * `REACT_APP_BANK_RECONCILE_ENABLED` ODER die Beta-UID-Allowlist greift
 * (`App.tsx`). Zusätzlich gated diese Seite ihren INHALT selbst über
 * `canUseBankReconcile` (Muster `DatevExportPage`/`MainLayout`
 * `canUseDatevExport`) — ab BR-3 hat die Seite eine echte Firestore-
 * Anbindung (Konten/Umsätze/Storage-Upload), ein reines Route-Gate reicht
 * daher nicht mehr aus (BR-0-Kommentar in `App.tsx:88-93`).
 *
 * @module BankReconciliationPage
 */

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IconBank, IconExchange, IconList, IconHelp } from '../icons';
import { useAuth } from '../context/AuthContext';
import { canUseBankReconcile, BANK_RECONCILE_FLAG } from '../config/bankReconcileAccess';
import { resolveTier } from '../config/entitlements';
import BankAccountsPanel from '../components/bank/BankAccountsPanel';
import TransactionsPanel from '../components/bank/TransactionsPanel';
import MatchingPanel from '../components/bank/MatchingPanel';
import WithoutReceiptPanel from '../components/bank/WithoutReceiptPanel';
import ImportWizard from '../components/bank/ImportWizard';
import FeatureTeaser from '../components/premium/FeatureTeaser';

type TabKey = 'accounts' | 'transactions' | 'matching' | 'without-receipt';

/**
 * PR-T6 (D7, §3.2): Upsell-Teaser für nicht-berechtigte Nutzer bei aktivem
 * `BANK_RECONCILE_FLAG` (Ziel-Tier Ultra). Ersetzt den generischen
 * „Coming-soon"-Platzhalter NUR wenn das Feature allgemein ausgerollt ist —
 * bei Flag AUS bleibt der bisherige Platzhalter unverändert (kein Kaufanreiz
 * für eine reine Beta-Erprobung, Muster `ReceiptScanPage`/§4.1).
 */
const BankReconcileTeaser: React.FC = () => (
  <FeatureTeaser
    icon={<IconBank size={30} aria-hidden />}
    badgeKey="bankReconcile.teaserBadge"
    badgeDefault="Ultra-Funktion"
    titleKey="bankReconcile.teaserTitle"
    titleDefault="Automatischer Kontoabgleich"
    descriptionKey="bankReconcile.teaserDescription"
    descriptionDefault="Importieren Sie Ihre Kontoumsätze per CAMT- oder CSV-Datei — FaeBill gleicht Zahlungseingänge automatisch mit offenen Rechnungen ab. Kein manuelles Abhaken mehr."
    highlights={[
      {
        labelKey: 'bankReconcile.teaserHighlight1',
        defaultLabel: 'Kontoumsätze per CAMT/CSV importieren',
      },
      {
        labelKey: 'bankReconcile.teaserHighlight2',
        defaultLabel: 'Automatischer Soll-Ist-Abgleich mit Rechnungen',
      },
      {
        labelKey: 'bankReconcile.teaserHighlight3',
        defaultLabel: 'Offene Posten in Sekunden schließen',
      },
    ]}
    ctaKey="bankReconcile.teaserCta"
    ctaDefault="Jetzt auf Ultra upgraden"
    promotionId="feature_teaser_bank_reconcile"
    creativeSlot="page"
  />
);

const BankReconciliationPage: React.FC = () => {
  const { t } = useTranslation('bank');
  const { effectiveScopeUid, sellerData } = useAuth();

  // Default-Tab „Abgleich" (§10.1). Fällt auf „Konten & Importe" zurück, wenn
  // noch keine Umsätze existieren, ist aber bewusst der Einstieg ins Feature.
  const [activeTab, setActiveTab] = useState<TabKey>('matching');
  const [wizardOpen, setWizardOpen] = useState(false);
  // Bumped after a successful import so BankAccountsPanel/TransactionsPanel reload.
  const [refreshToken, setRefreshToken] = useState(0);

  if (!canUseBankReconcile(effectiveScopeUid, resolveTier(sellerData))) {
    // PR-T6 (D7, §3.2): Flag AN + kein Ultra ⇒ FeatureTeaser (Upsell statt
    // Leerrendern). Flag AUS ⇒ byte-identisch zum bisherigen „Coming-soon"-
    // Platzhalter (nur die Beta-Allowlist hat dann überhaupt Zugriff, kein
    // Kaufanreiz für eine reine Beta-Erprobung).
    if (BANK_RECONCILE_FLAG) {
      return <BankReconcileTeaser />;
    }
    return (
      <div className="text-primary">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-primary">{t('pageTitle')}</h1>
          <p className="text-sm text-muted mt-1">{t('pageSubtitle')}</p>
        </div>
        <div className="bg-surface-raised border border-hairline rounded-xl p-8 text-center">
          <div className="text-4xl mb-4">&#x1F3E6;</div>
          <p className="text-secondary font-medium">{t('comingSoon.title')}</p>
          <p className="text-sm text-faint mt-2">{t('comingSoon.body')}</p>
        </div>
      </div>
    );
  }

  const tabs: Array<{ key: TabKey; label: string; icon: React.ReactNode }> = [
    { key: 'matching', label: t('tabs.matching', 'Abgleich'), icon: <IconExchange /> },
    { key: 'without-receipt', label: t('tabs.withoutReceipt', 'Ohne Beleg'), icon: <IconHelp /> },
    { key: 'transactions', label: t('tabs.transactions', 'Umsätze'), icon: <IconList /> },
    { key: 'accounts', label: t('tabs.accounts', 'Konten & Importe'), icon: <IconBank /> },
  ];

  const handleImportComplete = () => {
    setRefreshToken((v) => v + 1);
  };

  return (
    <div className="text-primary">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-primary">{t('pageTitle')}</h1>
        <p className="text-sm text-muted mt-1">{t('pageSubtitle')}</p>
      </div>

      <div className="flex gap-1 border-b border-hairline mb-6 overflow-x-auto">
        {tabs.map((tab) => (
          <button
            key={tab.key}
            type="button"
            onClick={() => setActiveTab(tab.key)}
            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
              activeTab === tab.key
                ? 'border-indigo-500 text-primary'
                : 'border-transparent text-muted hover:text-secondary'
            }`}
          >
            {tab.icon}
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === 'accounts' && (
        <BankAccountsPanel refreshToken={refreshToken} onOpenImportWizard={() => setWizardOpen(true)} />
      )}

      {activeTab === 'transactions' && <TransactionsPanel refreshToken={refreshToken} />}

      {activeTab === 'matching' && (
        <MatchingPanel
          refreshToken={refreshToken}
          onNavigateWithoutReceipt={() => setActiveTab('without-receipt')}
        />
      )}

      {activeTab === 'without-receipt' && <WithoutReceiptPanel refreshToken={refreshToken} />}

      <ImportWizard isOpen={wizardOpen} onClose={() => setWizardOpen(false)} onImportComplete={handleImportComplete} />
    </div>
  );
};

export default BankReconciliationPage;
